import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useActivePick } from '../hooks/useActivePick';
import { usePickHistory } from '../hooks/usePickHistory';

import Countdown from '../components/Countdown';
import LoadingSpinner from '../components/LoadingSpinner';
import PickHistoryRow from '../components/PickHistoryRow';
import { getPickStatusLabel } from '../utils/formatters';
import { ROUTES, LEAGUES } from '../utils/constants';
import { cancelPick } from '../services/pickService';
import toast from 'react-hot-toast';
import './MyPicksPage.css';

const DATE_FILTERS = [
  { key: 'all', label: 'All Picks' },
  { key: '7', label: 'Last 7 Days' },
  { key: '30', label: 'Last 30 Days' },
  { key: '90', label: 'Last 90 Days' },
];

export default function MyPicksPage() {
  const { activePick, loading: pickLoading, refetch: refetchPick } = useActivePick();
  const { picks: pickHistory, loading: historyLoading } = usePickHistory();
  const [cancelling, setCancelling] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [activePickGame, setActivePickGame] = useState(null);

  // Real-time listener for the active pick's game (for live score updates)
  useEffect(() => {
    if (!activePick?.gameId) {
      setActivePickGame(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'games', activePick.gameId), (snap) => {
      if (snap.exists()) {
        setActivePickGame({ id: snap.id, ...snap.data() });
      }
    });
    return () => unsub();
  }, [activePick?.gameId]);

  const handleCancelPick = async () => {
    setCancelling(true);
    try {
      await cancelPick();
      await refetchPick();
      toast.success('Pick cancelled');
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(err.message || 'Failed to cancel pick.');
    } finally {
      setCancelling(false);
    }
  };

  // Filter out cancelled picks, then apply date range filter
  const resolvedPicks = React.useMemo(() => {
    const nonCancelled = pickHistory.filter((p) => p.status !== 'cancelled');
    if (dateFilter === 'all') return nonCancelled;

    const days = parseInt(dateFilter, 10);
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

    return nonCancelled.filter((p) => {
      // resolvedAt or createdAt — handle Firestore Timestamps and Date objects
      const ts = p.resolvedAt || p.createdAt;
      if (!ts) return true;
      const ms = typeof ts.toMillis === 'function' ? ts.toMillis()
        : typeof ts.seconds === 'number' ? ts.seconds * 1000
        : new Date(ts).getTime();
      return ms >= cutoff;
    });
  }, [pickHistory, dateFilter]);

  return (
    <div className="my-picks-page">
      <h1>My Picks</h1>

      {/* Active Pick Section */}
      <div className="picks-section">
        <h2>Active Pick</h2>
        {pickLoading ? (
          <LoadingSpinner message="Loading your pick..." />
        ) : activePick ? (
          <div className="active-pick-card">
            <div className="active-pick-details">
              <div className="active-pick-team">
                {activePick.pickTeamLogo && (
                  <img src={activePick.pickTeamLogo} alt="" className="active-pick-logo" />
                )}
                <div className="active-pick-info">
                  <span className="active-pick-name">{activePick.pickTeamName}</span>
                  <span className="active-pick-vs">vs {activePick.opponentTeamName}</span>
                  <span className="active-pick-league">
                    {LEAGUES[activePick.leagueSlug]?.emoji} {activePick.league}
                  </span>
                </div>
              </div>
              <div className="active-pick-status">
                {activePickGame && (activePickGame.status === 'in_progress' || activePickGame.status === 'final') ? (
                  <>
                    <span className="active-pick-score">
                      {activePickGame.awayTeam?.abbreviation} {activePickGame.awayScore ?? 0}
                      {' '}&ndash;{' '}
                      {activePickGame.homeTeam?.abbreviation} {activePickGame.homeScore ?? 0}
                    </span>
                    {activePickGame.statusDetail &&
                      !activePickGame.statusDetail.toLowerCase().includes('scheduled') && (
                      <span className="active-pick-time">{activePickGame.statusDetail}</span>
                    )}
                  </>
                ) : activePick.status === 'pending' ? (
                  <>
                    <span className="countdown-label">Starts in</span>
                    <Countdown targetTime={activePick.gameStartTime} />
                  </>
                ) : (
                  <span className={`status-badge status-${activePick.status}`}>
                    {getPickStatusLabel(activePick.status)}
                  </span>
                )}
              </div>
            </div>
            {activePick.status === 'pending' && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleCancelPick}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Pick'}
              </button>
            )}
          </div>
        ) : (
          <div className="no-pick-card">
            <p>No active pick right now.</p>
            <Link to={ROUTES.GAMES} className="btn btn-primary btn-sm">
              Browse Games
            </Link>
          </div>
        )}
      </div>

      {/* Pick History Section */}
      <div className="picks-section">
        <div className="history-header">
          <h2>Pick History</h2>
          <select
            className="date-filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            {DATE_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        {historyLoading ? (
          <LoadingSpinner message="Loading history..." />
        ) : resolvedPicks.length === 0 ? (
          <div className="no-pick-card">
            <p>No completed picks yet. Make a pick and check back after the game!</p>
          </div>
        ) : (
          <div className="pick-history-table-wrap">
            <table className="pick-history-table">
              <thead>
                <tr>
                  <th>Sport</th>
                  <th>Team</th>
                  <th>Result</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {activePick && activePick.status === 'locked' && (
                  <PickHistoryRow key={activePick.id} pick={activePick} isLive activePickGame={activePickGame} />
                )}
                {resolvedPicks.map((pick) => (
                  <PickHistoryRow key={pick.id} pick={pick} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
